import CheckIcon from '@mui/icons-material/Check'
import { Stack, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { BackContinue } from '@components/back_continue/back_continue'
import { RoomSelector } from '@components/room_selector/room_selector'
import { useBookingContext } from '@providers/booking/booking_context'
import { useBookingContinue } from '@providers/booking_continue'
import { useLocationContext } from '@providers/location'
import { useSessionContext } from '@providers/session'
import { isUserLoggedIn } from '@providers/session/helpers'
import { joinPetNames } from '@shared/booking_groups'
import { MAX_WIDGET_SIZE, REQUIRED_ADDONS_ALERT_HEIGHT_PX, STICKY_FOOTER_HEIGHT_PX } from '@shared/constants'
import { buildSearchParams } from '@shared/header_navigation'
import { i18WithParams as t } from '@shared/locale'
import { isWaitlistEnabled } from '@shared/locations'
import { captureError } from '@shared/monitoring'
import { useRoutes } from 'app/use_routes'
import { BoardingIcon } from 'components-ui/src/asset/boarding_icon'
import { DaycareIcon } from 'components-ui/src/asset/daycare_icon'
import { getBaseGroup } from 'components-ui/src/offer_card/helper'
import { OfferCard } from 'components-ui/src/offer_card/offer_card'
import type { TOfferSelectionOptions } from 'components-ui/src/offer_card/offer_card.d'
import { Spacer } from 'components-ui/src/spacer/spacer'
import { useRedirectNoData } from 'hooks/use_redirect_no_data'
import { useClientBookingSearch } from 'service-api/src/hooks/use_client_booking_search'
import { AvailabilityGroupClientDto, BookingSearchClientDto, OfferClientDto } from 'service-api/src/types/api'
import {
  allGroupsSelectionCompleted,
  configureTemperTest,
  getRoomPriceForWaitlist,
  isGroupSelectionCompleted,
  listPetNames,
  setUpTemperTest,
  sortOffers,
} from './helper'
import { RequiredAlert } from './required_addons/required_alert'
import { SelectRequiredAddonsWrapper } from './required_addons/select_required_addons_wrapper'
import { SearchOffersMembershipAlert } from './search_offers_membership_alert'
import { SearchResultsSkeleton } from './search_result_skeleton'
import { PendingOfferSelection } from './types'
import { WaitlistModal } from './waitlist_modal'

const SearchOfferResults = ({
  offers,
  showWarning,
  onSelectScheduleTempTest,
}: {
  offers: BookingSearchClientDto[]
  showWarning: boolean
  onSelectScheduleTempTest: (affectedByPetIds: string[]) => Promise<void>
}) => {
  const { accountName = '', serviceTypeName = '' } = useParams()
  const bookingCtx = useBookingContext()
  const sessionCtx = useSessionContext()
  const { location } = useLocationContext()
  const { navigateRoute } = useRoutes()
  const [currentTabIndex, setTabIndex] = useState(0)
  const [canContinueNavigation, setCanContinueNavigation] = useState(false)
  const theme = useTheme()
  const onChangeTab = useCallback((_label: string, index: number) => {
    setTabIndex(index)
    bookingCtx.dispatch({ type: 'SET_PET_GROUP', payload: { value: index } })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [pendingOfferSelection, setPendingOfferSelection] = useState<PendingOfferSelection | null>(null)
  const waitlistEnabled = (location?.subscriberGroup?.waitlistEnabled ?? false) && isWaitlistEnabled(bookingCtx.state)

  const sortedOffers = useMemo(() => {
    if (offers) {
      return sortOffers(offers, serviceTypeName, currentTabIndex)
    } else {
      return []
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offers])

  const tabs = useMemo(() => {
    const groups = bookingCtx.state.cart.groups
    return joinPetNames(groups).map((pet, idx) => {
      const group = groups[idx]
      return {
        label: pet,
        room: `(${t('global.label.room')} ${idx + 1})`,
        icon: isGroupSelectionCompleted(group) ? <CheckIcon fontSize="small" /> : undefined,
      }
    })
  }, [bookingCtx.state.cart.groups])

  // keeps the offers selected by the user for each group
  const [groupOffer, setGroupOffer] = useState<Record<number, BookingSearchClientDto>>(() => {
    return bookingCtx.state.cart.groups.reduce((acc, g, groupIdx) => {
      const selected = sortedOffers.find((r) => r.offer?.id === g.offerSelected?.offer?.id)
      if (selected) {
        acc[groupIdx] = selected
      }
      return acc
    }, {} as GenericSimpleBag)
  })
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))

  useEffect(() => {
    if (!canContinueNavigation) {
      return
    }
    if (!isUserLoggedIn(sessionCtx.state.user)) {
      navigateRoute('loginCheck', { accountName, serviceTypeName })
      return
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canContinueNavigation, sessionCtx.state.user])

  const onSelectOfferConfirm = useCallback(
    (
      offer: OfferClientDto,
      availability: AvailabilityGroupClientDto,
      petGroupIdx: number,
      options: TOfferSelectionOptions
    ) => {
      bookingCtx.dispatch({
        type: 'SELECT_OFFER',
        payload: {
          offer,
          availability,
          petGroup: petGroupIdx,
          hasRequiredAddons: options.hasRequiredAddons ?? false,
          bookWaitlist: waitlistEnabled && options.type === 'waitlist',
        },
      })
      setGroupOffer({ ...groupOffer, [petGroupIdx]: sortedOffers.find((r) => r.offer?.id === offer.id)! })
      if (!options.holdNavigation) {
        setCanContinueNavigation(true)
      }
    },
    [bookingCtx, groupOffer, sortedOffers, waitlistEnabled]
  )

  const onSelectOffer = useCallback(
    (
      offer: OfferClientDto,
      availability: AvailabilityGroupClientDto,
      petGroupIdx: number,
      options: TOfferSelectionOptions
    ) => {
      if (options.type === 'waitlist') {
        setPendingOfferSelection({
          offer,
          availability,
          petGroupIdx,
          options,
        })
        return
      }

      onSelectOfferConfirm(offer, availability, petGroupIdx, options)
    },
    [onSelectOfferConfirm]
  )

  const confirmWaitlistSelection = useCallback(async () => {
    if (!pendingOfferSelection) return

    onSelectOfferConfirm(
      pendingOfferSelection.offer,
      pendingOfferSelection.availability,
      pendingOfferSelection.petGroupIdx,
      pendingOfferSelection.options
    )

    setPendingOfferSelection(null)
  }, [onSelectOfferConfirm, pendingOfferSelection])

  const renderCard = (result: BookingSearchClientDto) => {
    const offerSelected = bookingCtx.state.cart.groups[currentTabIndex]?.offerSelected
    const isSelected = offerSelected?.offer?.id === result.offer?.id
    return (
      <Fragment key={result.offer!.id}>
        <Paper elevation={1} sx={{ p: 2, borderRadius: `${isBelowMD ? 0 : theme.shape.borderRadius}px` }}>
          <OfferCard
            key={result.offer!.id}
            searchResult={result}
            petGroup={currentTabIndex}
            onSelectOffer={onSelectOffer}
            serviceTypeName={serviceTypeName}
            specieTypeName={bookingCtx.state.cart.groups[0].specieName}
            selected={isSelected}
            hasMembershipsService={!!bookingCtx.state.data.membershipService}
            temperTest={bookingCtx.state.data.temperTest}
            onSelectScheduleTempTest={onSelectScheduleTempTest}
            petNames={listPetNames(bookingCtx.state, currentTabIndex)}
            InnerComponent={<SelectRequiredAddonsWrapper enabled={isSelected} />}
            subscriberGroup={{ waitlistEnabled }}
          />
        </Paper>
        <Spacer height={3} />
      </Fragment>
    )
  }

  const renderResult = () => {
    const petGroups = bookingCtx.state.cart.groups

    return sortedOffers.map((result) => {
      if (result.offer?.locationSpecies?.id !== petGroups?.[currentTabIndex]?.specieId) {
        return null
      }
      return renderCard(result)
    })
  }

  const renderWaitlistModal = () => {
    if (!pendingOfferSelection) return null

    const groupIdx = pendingOfferSelection.petGroupIdx || 0
    const group = getBaseGroup(serviceTypeName, groupIdx, [pendingOfferSelection.availability])

    const strategyStr =
      group.pricingStrategy === 'NIGHTLY' ? t('global.label.strategy.night') : t('global.label.strategy.day')

    const price = getRoomPriceForWaitlist(group, false)

    return (
      <WaitlistModal
        onClose={async () => setPendingOfferSelection(null)}
        onConfirm={confirmWaitlistSelection}
        price={price}
        strategy={strategyStr}
        multiplePets
      />
    )
  }

  return (
    <Box>
      {tabs.length > 1 && (
        <>
          <RoomSelector tabIndex={currentTabIndex} tabs={tabs} onChange={onChangeTab} />
          <Spacer height={3} />
        </>
      )}
      <Box sx={{ paddingBottom: `${STICKY_FOOTER_HEIGHT_PX + (showWarning ? REQUIRED_ADDONS_ALERT_HEIGHT_PX : 0)}px` }}>
        {renderResult()}
      </Box>
      {pendingOfferSelection && renderWaitlistModal()}
    </Box>
  )
}

export const SearchOffersGeneral = () => {
  const { accountName = '', serviceTypeName = '' } = useParams()
  const bookingCtx = useBookingContext()
  const sessionCtx = useSessionContext()
  const { continueBooking, isContinuing } = useBookingContinue()
  const { navigateRoute } = useRoutes()
  const theme = useTheme()
  const [isSchedulingTemperTest, setIsSchedulingTemperTest] = useState(false)
  const searchParams = useMemo(() => buildSearchParams(bookingCtx), [bookingCtx])

  const searchResult = useClientBookingSearch(accountName, serviceTypeName, {
    ...searchParams,
    offersType: 'PRIMARY',
    enabled: !isSchedulingTemperTest,
  })

  const onSelectScheduleTempTest = useCallback(
    async (affectedByPetIds: string[]) => {
      if (!isUserLoggedIn(sessionCtx.state.user)) {
        navigateRoute('loginCheck', { accountName, serviceTypeName })
        return
      }

      setIsSchedulingTemperTest(true)
      try {
        setUpTemperTest(bookingCtx.state, bookingCtx.dispatch, affectedByPetIds)
        await configureTemperTest({
          temperTestId: bookingCtx.state.data.temperTest?.id || '',
          dispatch: bookingCtx.dispatch,
          accountName,
        })
        setTimeout(() => navigateRoute('temperTest', { serviceTypeName: 'temper-test' }), 0)
      } catch (e) {
        captureError({ cause: e } as Error)
      }
    },
    [accountName, bookingCtx, navigateRoute, serviceTypeName, sessionCtx.state.user]
  )

  useEffect(() => {
    if (searchResult.isError) {
      captureError(searchResult.error as Error)
    }
  }, [searchResult.error, searchResult.isError])

  useEffect(() => {
    if (searchResult.isSuccess) {
      bookingCtx.dispatch({ type: 'LOAD_OFFERS_SEARCH_RESULTS', payload: { value: searchResult.data?.results || [] } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResult.isSuccess, searchResult.data?.results])

  useEffect(() => {
    if (bookingCtx.state.currentState !== 'searchResults') {
      bookingCtx.dispatch({ type: 'SET_SEARCH_STATE', payload: { value: 'searchResults' } })
    }
  })

  // redirect to home if the state has invalid data
  useRedirectNoData()

  const pageTitle =
    bookingCtx.state.serviceTypeName === 'boarding' ? 'searchResults.title.boarding' : 'searchResults.title.daycare'

  const iconTitle =
    bookingCtx.state.serviceTypeName === 'boarding' ? (
      <BoardingIcon color="primary" sx={{ width: 40, height: 40 }} />
    ) : (
      <DaycareIcon color="primary" fill={theme.palette.primary.main} sx={{ width: 40, height: 40 }} />
    )

  const onBackHandler = useCallback(() => {
    navigateRoute('searchServiceType', { serviceTypeName })
  }, [navigateRoute, serviceTypeName])

  const onContinueHandler = useCallback(async (): Promise<boolean> => {
    await continueBooking()
    return false
  }, [continueBooking])

  const canContinue = useMemo(() => {
    const groups = bookingCtx.state.cart.groups
    return allGroupsSelectionCompleted(groups)
  }, [bookingCtx.state.cart.groups])

  const showWarning = useMemo(() => {
    const groups = bookingCtx.state.cart.groups
    if (allGroupsSelectionCompleted(groups)) {
      return false
    }
    return groups.some((group) => group.offerSelected)
  }, [bookingCtx.state.cart.groups])

  return (
    <Box sx={{ maxWidth: MAX_WIDGET_SIZE, margin: '0 auto' }}>
      <Spacer height={3} />
      <Stack direction="row" alignItems="center">
        {iconTitle}
        <Typography variant="h2">{t(pageTitle)}</Typography>
      </Stack>
      <Spacer height={3} />
      <SearchOffersMembershipAlert
        hasMembershipsService={!!bookingCtx.state.data.membershipService}
        searchResults={searchResult?.data?.results}
      />
      {searchResult.isLoading ? (
        <SearchResultsSkeleton />
      ) : (
        <SearchOfferResults
          offers={searchResult.data?.results || []}
          showWarning={showWarning}
          onSelectScheduleTempTest={onSelectScheduleTempTest}
        />
      )}

      {showWarning && <RequiredAlert />}
      <BackContinue
        onBack={onBackHandler}
        onContinue={onContinueHandler}
        continueEnabled={canContinue && !isContinuing}
      />
    </Box>
  )
}
